import { Component, EventEmitter, inject, Input, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatRippleModule } from '@angular/material/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { EventsService } from '@services/events/events.service';
import { FundraiserService } from '@services/fundraiser/fundraiser.service';
import { UtilService } from '@services/util/util.service';
import { VariablesService } from '@services/variables/variables.service';
import { PaymentUtilService } from '@services/payment-util/payment-util.service';
import { Bulk_Min_Amount } from '@models/bulk-min-amount';
import { InrFormatPipe } from '@pipes/inr-format/inr-format.pipe';
import { ImgFallbackDirective } from '@directives/img-fallback/img-fallback.directive';
import { CurrencyIconComponent } from '@core-components/currency-icon/currency-icon.component';
import { CustomDatePipe } from '@pipes/custom-date/custom-date.pipe';
import { ProgressBarComponent } from '@common-components/progress-bar/progress-bar.component';

@Component({
  selector: 'app-bulk-donation-selection',
  standalone: true,
  imports: [
    CommonModule, MatButtonModule, MatRippleModule, InrFormatPipe,
    ProgressBarComponent,
    ImgFallbackDirective,
    CustomDatePipe,
    CurrencyIconComponent
  ],
  templateUrl: './bulk-donation-selection.component.html',
  styleUrls: ['./bulk-donation-selection.component.scss']
})
export class BulkDonationSelectionComponent {

  readonly vars = inject(VariablesService);

  private bottomSheet = inject(MatBottomSheet);
  private events = inject(EventsService);
  private fundraiserService = inject(FundraiserService);
  private paymentUtil = inject(PaymentUtilService);
  public util = inject(UtilService);

  @Input() amount;
  @Input() currency;
  @Input() campaign_id;
  @Input() disabled;
  @Input() isOrderLoading;
  @Output() onSubmit = new EventEmitter();

  @ViewChild('bulkDonation', { static: true }) bulkDonation;

  bulkData: any = {};
  minamt = 0;
  selected = [];
  totalamt;
  campaigns: any[];
  btnlabel;
  isAnySelected = false;
  isCampaignLoading: boolean = false;
  minamount = Bulk_Min_Amount;

  ngOnInit() {
    this.util.addCssToGlobal('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap');
    this.util.addCssToGlobal('https://fonts.googleapis.com/css2?family=Source+Sans+Pro:wght@400;600;700;800;900&display=swap');
  }

  handleContinueCta(event) {
    if (event) {
      event.preventDefault();
    }
    this.getCampaigns(this.currency);
  }

  getCampaigns(currency) {
    this.isCampaignLoading = true;
    const qpram = `?campaignId=${this.vars.fundraiser?.id}&causeId=${this.vars.fundraiser?.cause_id}&limit=3&tags=featured&with=beneficiaryname;storyWidget&orderBy=priority_entity&sortBy=ASC&currency=` + currency;
    this.fundraiserService.getUrgentStoryCampaigns(qpram).subscribe((res: any) => {
      const camp_list: any[] = res.data.data;
      this.campaigns = camp_list.filter(item => this.campaign_id !== item?.id).filter((item, i) => i < 2);
      this.campaigns = this.campaigns.map((val, i) => {
        if (val?.raised?.raised && val?.amount_requested) {
          val['amount_remaining'] = val?.amount_requested - val?.raised?.raised;
          if (val?.amount_remaining < 0) {
            val['amount_remaining'] = '0';
          }
        }
        return val;
      });
      this.setBtnLabel();
      if (camp_list.length === 0) {
        this.onSubmit.emit(false);
      } else {
        this.openSheet()
      }
      this.isCampaignLoading = false;
    });
  }

  openSheet() {
    this.btnlabel = `Skip and Contribute ${this.util.formatAmount(this.amount, this.currency)}`;

    if (this.disabled) {
      this.onSubmit.emit(false);
      return false;
    }

    this.bottomSheet.open(this.bulkDonation, {
      panelClass: 'bulk-sheet'
    });
  }

  closeSheet() {
    this.paymentUtil.cartResponse.subscribe((res) => {
      if (res) {
        this.bottomSheet.dismiss(this.bulkDonation);
      }
    });
  }

  hide(id) {
    this.selected.push(id);
    this.bulkData[id] = { amount: this.minamt };
    if (this.bulkData[id].amount === 0) {
      this.bulkData[id].amount = this.bulkData[id].amount + this.minamount[this.currency].minamt;
    }
    this.setBtnLabel();
    this.events.sendSystemEvent({
      eventName: 'amountinc',
    }).subscribe(_ => _);
  }

  amtchanger(value, id) {
    if (value === 'plus' && this.bulkData[id]?.amount < this.minamount[this.currency]?.minamt * 20) {
      this.bulkData[id].amount = this.bulkData[id].amount + this.minamount[this.currency].incramt;
    }
    if (value === 'minus') {
      if (this.bulkData[id].amount > this.minamount[this.currency].minamt) {
        this.bulkData[id].amount = this.bulkData[id].amount - this.minamount[this.currency].incramt;
      } else {
        this.events.sendSystemEvent({
          eventName: 'amountdec',
        }).subscribe(_ => _);
        this.selected.splice(this.selected.indexOf(id), 1);
        this.bulkData[id].amount = 0;
        delete this.bulkData[id];
      }
    }
    this.setBtnLabel();
  }

  setBtnLabel() {
    this.totalamt = Object.values(this.bulkData).reduce((a, { amount }) => a + amount, 0);
    if (this.totalamt > 0) {
      this.isAnySelected = true;
      this.btnlabel = `Contribute ${this.util.formatAmount(this.amount, this.currency)} + ${this.util.formatAmount(this.totalamt, this.currency)}`;
    } else {
      this.isAnySelected = false;
      this.btnlabel = `Skip and Contribute ${this.util.formatAmount(this.amount, this.currency)}`;
    }
  }

  submit() {
    if (Object.keys(this.bulkData).length === 0) {

      this.onSubmit.emit(false);
    } else {
      this.onSubmit.emit(this.bulkData);
    }
    this.closeSheet();
  }
}
